/**
 * 库存状态实现 —— 用 Zustand + persist(localStorage) 替代 Swift LibraryStore + UserDefaults。
 * 行为与 Swift 端完全一致：收藏切换、最近浏览去重/倒序/截断 limit。
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  LIBRARY_HISTORY_LIMIT,
  LIBRARY_SEARCH_HISTORY_LIMIT,
  LIBRARY_STORAGE_KEY,
  type LibraryState,
  type RecentVisit,
} from '@/core/libraryStore';

interface PersistedShape {
  favoriteIDs: string[];
  recentVisits: RecentVisit[];
  recentSearches: string[];
}

export const useLibraryStore = create<LibraryState>()(
  persist(
    (set, get) => ({
      favoriteIDs: [],
      recentVisits: [],
      recentSearches: [],

      isFavorite(entryID) {
        return get().favoriteIDs.includes(entryID);
      },

      toggleFavorite(entryID) {
        set((state) => {
          const exists = state.favoriteIDs.includes(entryID);
          return {
            favoriteIDs: exists
              ? state.favoriteIDs.filter((id) => id !== entryID)
              : [...state.favoriteIDs, entryID],
          };
        });
      },

      recordVisit(entryID) {
        set((state) => {
          const visitedAt = new Date().toISOString();
          // 去重：移除已有记录，再插到队首（对应 Swift 移除旧记录后 prepend）。
          const remaining = state.recentVisits.filter((v) => v.entryID !== entryID);
          const next = [{ entryID, visitedAt }, ...remaining];
          // 截断到 historyLimit。
          if (next.length > LIBRARY_HISTORY_LIMIT) {
            next.length = LIBRARY_HISTORY_LIMIT;
          }
          return { recentVisits: next };
        });
      },

      clearHistory() {
        set({ recentVisits: [] });
      },

      recordSearch(query) {
        const normalized = query.trim();
        if (!normalized) return;
        set((state) => {
          const remaining = state.recentSearches.filter((item) => item !== normalized);
          const next = [normalized, ...remaining];
          if (next.length > LIBRARY_SEARCH_HISTORY_LIMIT) {
            next.length = LIBRARY_SEARCH_HISTORY_LIMIT;
          }
          return { recentSearches: next };
        });
      },

      clearRecentSearches() {
        set({ recentSearches: [] });
      },
    }),
    {
      name: LIBRARY_STORAGE_KEY,
      // 仅持久化数据字段，方法由 store 重建。
      partialize: (state): PersistedShape => ({
        favoriteIDs: state.favoriteIDs,
        recentVisits: state.recentVisits,
        recentSearches: state.recentSearches,
      }),
    },
  ),
);
